import { Inbox } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import type { ExtractionIncomingPaper } from "@/features/data-curation/types";

const citationHref = (documentId: string) => {
  const root = documentId.startsWith("fda_")
    ? "fda"
    : /^NCT\d{8}$/i.test(documentId)
      ? "trials"
      : "papers";
  return `/citations/${root}/${encodeURIComponent(documentId)}`;
};

export const CurationIncoming = ({
  papers,
  total,
}: {
  papers: ExtractionIncomingPaper[];
  total: number;
}) => {
  if (total === 0) return null;
  return (
    <Card aria-labelledby="curation-incoming-heading" role="region">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-zinc-100 px-4 py-3">
        <h2
          id="curation-incoming-heading"
          className="flex items-center gap-2 text-sm font-medium text-zinc-900"
        >
          <Inbox className="h-4 w-4 text-zinc-500" aria-hidden />
          Incoming
        </h2>
        <p className="text-xs text-zinc-500" aria-live="polite">
          {total === 1
            ? "1 finished paper has no row yet."
            : `${total} finished papers have no rows yet.`}
        </p>
      </div>
      <ul className="divide-y divide-zinc-100">
        {papers.map((paper) => {
          const rowNames = paper.row_names ?? paper.groups ?? [];
          return (
            <li key={`${paper.map_id}:${paper.task_id}`} className="px-4 py-2">
              <Link
                to={citationHref(paper.document_id)}
                className="break-all font-mono text-xs text-zinc-900 underline decoration-zinc-300 underline-offset-2"
              >
                {paper.document_id}
              </Link>
              {rowNames.length > 0 && (
                <p className="mt-0.5 text-xs text-zinc-500">
                  {rowNames.join(" · ")}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {total > papers.length && (
        <p className="border-t border-zinc-100 px-4 py-2 text-xs text-zinc-500">
          Showing {papers.length} of {total}.
        </p>
      )}
    </Card>
  );
};
